import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Footprints, Gauge } from 'lucide-react';
import type { SegmentStatistics } from '@/types/statistics';
import { formatDistance, formatSpeedKmh } from '@/lib/utils';
import type { PlayerColor } from './playerColors';
import { VersusBars } from './VersusBars';
import { InfoSheet } from './InfoSheet';
import CardHead from './CardHead';
import Donut from './Donut';
import { resolveTeams } from './tournamentMetrics';
import {
  depthOccupancy,
  resolveTeamFormationVersus,
  DEPTH_COLORS,
  type DepthKey,
} from './positionMetrics';

// The Visão Geral "Position" card: the focus player's headline movement (total
// distance + average speed), a depth-occupancy donut (net / transition / back),
// and a team-vs-opponents head-to-head on formation sync + court coverage.
interface PositionCardProps {
  segment: SegmentStatistics;
  crops: Record<string, string>;
  colors: Record<string, PlayerColor>;
  /** The player the card is scoped to (the identified viewer). */
  focusPlayerId: string;
}

const PositionCard: React.FC<PositionCardProps> = ({
  segment,
  crops,
  colors,
  focusPlayerId,
}) => {
  const { t } = useTranslation('tournament');
  const tl = (k: string) => t(`label.${k}` as never) as string;
  const [syncInfo, setSyncInfo] = useState(false);
  const stats = segment.statistics;
  const ps = stats.players_statistics[focusPlayerId];
  if (!ps) return null;

  const dist = formatDistance(ps.running_distance);
  const speed = formatSpeedKmh(ps.mean_velocity);

  const depth = depthOccupancy(ps.court_zone.front_back);
  const depthLabel: Record<DepthKey, string> = {
    net: t('overview.position.net'),
    transition: t('overview.position.transition'),
    back: t('overview.position.back'),
  };

  // Team-vs-opponents formation (hidden when formation data is absent, e.g.
  // basic reports). Resolve the rosters here too, for the comparison avatars.
  const versus = resolveTeamFormationVersus(
    stats.teams,
    stats.formation_analysis,
    focusPlayerId,
  );
  const roster = resolveTeams(stats.teams, focusPlayerId);
  const yourMembers = roster?.yourMembers ?? [];
  const oppMembers = roster?.oppMembers ?? [];
  const yourColor = colors[focusPlayerId]?.solid ?? 'var(--accent)';
  const oppColor = colors[oppMembers[0]]?.solid ?? 'var(--muted-2)';
  const yourCrops = yourMembers.map((id) => crops[id]).filter(Boolean);
  const oppCrops = oppMembers.map((id) => crops[id]).filter(Boolean);
  const pct = (v: number) => `${Math.round(v * 100)}%`;
  const secs = (v: number) => `${v.toFixed(1)}s`;

  return (
    <div className="card position-card">
      <CardHead
        icon={Footprints}
        title={t('overview.position.title')}
        subtitle={t('overview.position.subtitle')}
        onInfo={versus ? () => setSyncInfo(true) : undefined}
        infoLabel={t('overview.position.title')}
      />

      {/* Headline movement — distance + average speed (km/h). */}
      <div className="position-tiles">
        <div className="partida-tile">
          <span className="partida-tile-badge">
            <Footprints size={16} strokeWidth={2} />
          </span>
          <div className="partida-tile-val">
            <span className="partida-tile-num">{dist.value}</span>
            <span className="partida-tile-unit">{dist.unit}</span>
          </div>
          <div className="partida-tile-label">{tl('distance')}</div>
        </div>
        <div className="partida-tile">
          <span className="partida-tile-badge">
            <Gauge size={16} strokeWidth={2} />
          </span>
          <div className="partida-tile-val">
            <span className="partida-tile-num">{speed.value}</span>
            <span className="partida-tile-unit">{speed.unit}</span>
          </div>
          <div className="partida-tile-label">{tl('avgSpeed')}</div>
        </div>
      </div>

      {/* Depth occupancy — net / transition / back, as a donut + legend. */}
      {depth ? (
        <div className="position-depth">
          <Donut
            segments={depth.map((s) => ({
              key: s.key,
              pct: s.pct,
              color: DEPTH_COLORS[s.key],
            }))}
          />
          <ul className="position-depth-legend">
            {depth.map((s) => (
              <li key={s.key} className="position-depth-item">
                <span
                  className="position-depth-dot"
                  style={{ background: DEPTH_COLORS[s.key] }}
                />
                <span className="position-depth-pct">{s.pct}%</span>
                <span className="position-depth-name">{depthLabel[s.key]}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Team vs opponents, in two head-to-heads sharing one roster: who owned
          the net, then how well each pair moved as a unit. Same metrics (and
          same copy) as the In Depth teams view, summarised here so the net
          battle doesn't require switching tabs to find. */}
      {versus ? (
        <>
          <div className="position-versus">
            <div className="position-subhead">{tl('netDom')}</div>
            <VersusBars
              left={{
                name: t('overview.position.yourTeam'),
                color: yourColor,
                crops: yourCrops,
              }}
              right={{
                name: tl('opponents'),
                color: oppColor,
                crops: oppCrops,
              }}
              metrics={[
                {
                  label: tl('netTime'),
                  left: versus.your.netTime,
                  right: versus.opp.netTime,
                  fmt: pct,
                },
                {
                  label: tl('conquests'),
                  left: versus.your.conquests,
                  right: versus.opp.conquests,
                  fmt: (v) => v.toFixed(1),
                },
                {
                  label: tl('netHold'),
                  left: versus.your.hold,
                  right: versus.opp.hold,
                  fmt: secs,
                },
                {
                  label: tl('advance'),
                  left: versus.your.advance,
                  right: versus.opp.advance,
                  fmt: secs,
                },
              ]}
            />
          </div>

          <div className="position-versus">
            {/* Names both bars below it — `label.sync` alone reads as only the
                first one, and it's shared with the In Depth card. */}
            <div className="position-subhead">
              {t('overview.position.syncGroup')}
            </div>
            <VersusBars
              left={{
                name: t('overview.position.yourTeam'),
                color: yourColor,
                crops: yourCrops,
              }}
              right={{
                name: tl('opponents'),
                color: oppColor,
                crops: oppCrops,
              }}
              metrics={[
                {
                  label: t('overview.position.sync'),
                  left: versus.your.sync,
                  right: versus.opp.sync,
                  fmt: pct,
                },
                {
                  label: t('overview.position.coverage'),
                  left: versus.your.coverage,
                  right: versus.opp.coverage,
                  fmt: pct,
                },
              ]}
            />
          </div>
        </>
      ) : null}

      {/* One sheet for both head-to-heads — same copy as the In Depth "Domínio
          da rede" and "Sincronização" cards, so a metric never reads two
          different ways depending on where it was found. */}
      <InfoSheet
        open={syncInfo}
        onClose={() => setSyncInfo(false)}
        title={t('overview.position.title')}
        metrics={[
          {
            name: tl('netTime'),
            description: tl('netTimeDesc'),
            higherIsBetter: true,
          },
          {
            name: tl('conquests'),
            description: tl('conquestsDesc'),
            higherIsBetter: true,
          },
          {
            name: tl('netHold'),
            description: tl('holdDesc'),
            higherIsBetter: true,
          },
          {
            name: tl('advance'),
            description: tl('advanceDesc'),
            higherIsBetter: false,
          },
          {
            name: t('overview.position.sync'),
            description: tl('depthSyncDesc'),
            higherIsBetter: true,
          },
          {
            name: t('overview.position.coverage'),
            description: tl('coverageDesc'),
            higherIsBetter: true,
          },
        ]}
      />
    </div>
  );
};

export default PositionCard;
